import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2023 semantics GmbH
//  Date      : $Date: 2026-03-13 15:47:48 +0100 (Fri, 13 Mar 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 161511 $
//  Purpose   : viewer for many pages
// =============================================================================
// ---Third-party imports
import React, { useEffect } from 'react';
import clsx from 'clsx';
// ---Framework imports
import { isNil, isString, size } from 'ldash';
import log from 'uix/logging';
import { PageSetViewer } from 'uix/viewer/components';
import { useFullscreen, useInView, useDelay, useState, useSelector } from 'uix/hooks';
import { Button, ButtonGroup, VIcon } from 'uix/components';
// ---Local imports
import S from './pageViewer.css';
import { useCaptionLink } from '../hooks';
import { SplitPane } from 'uix/components';
import { FulltextRenderPane } from 'uix/wiki/components';
// ---Exports
const useNavigation = ({ navigationControls, viewerData, setScrollToPageId, optionSet })=>{
    let [centerSelected, setCenterSelected] = useState(null);
    let onCenterSelectionChange = ({ otid })=>{
        //~ log.x('%o', {otid, viewerData})
        setCenterSelected(otid);
    };
    let navigate = (change_idx)=>{
        let idx = viewerData.index?.byId[centerSelected]?.flatIdx;
        if (isNil(idx)) return;
        let pageIds = viewerData.index?.pageIds || [];
        let next_idx = Math.max(0, Math.min(change_idx(idx), pageIds.length - 1));
        let otid = viewerData.index.pageIds.at(next_idx);
        if (otid) {
            setScrollToPageId(otid);
        }
    };
    let delta = {
        v2: 2,
        v4: 4
    }[optionSet] || 1;
    let onForwardClick = ()=>navigate((idx)=>idx + delta);
    let onBackwardClick = ()=>navigate((idx)=>idx - delta);
    return {
        onCenterSelectionChange,
        centerSelected,
        onBackwardClick,
        onForwardClick
    };
};
const useViewerControl = ()=>{
    let [viewerInstance, setViewerInstance] = useState(null);
    let zoomIn = ()=>{
        viewerInstance.setZoom(viewerInstance.state.zoom * 1.4);
    };
    let zoomOut = ()=>{
        viewerInstance.setZoom(viewerInstance.state.zoom * 1.4 ** -1);
    };
    return {
        onViewerInstance: setViewerInstance,
        zoomIn,
        zoomOut
    };
};
const ControlButton = ({ icon, onClickFunc })=>{
    return /*#__PURE__*/ _jsx(Button, {
        size: "tiny",
        className: S.controlButton,
        bordered: true,
        icon: icon,
        onClick: onClickFunc
    });
};
export const PageViewer = ({ otid, otids, linkid, caption, width, mobile, height, optionSet, initialZoomMode, initAni, initAniWait, initAniDuration, initAniScale, dpiAware, gutterX, gutterY, paginationMode, clsHover, wheelMode, wheelModeMeta, fullscreenToggle, navigationControls, zoomControls, showFulltext, imageClick, onCenterChange, addContent, skipFirstPage })=>{
    let [inviewRef, inView] = useInView({
        triggerOnce: false
    });
    let { ref, toggleFullscreen, isFullscreen } = useFullscreen();
    let [hasSkipped, setHasSkipped] = useState(false);
    let options = {
        zoom: {},
        layout: {
            dpiAware
        },
        render: {}
    };
    let merge_opt = (options, name, value)=>{
        if (isNil(value)) return options // do not inject if null/undefined
        ;
        let [n1, n2] = name.split('.');
        return {
            ...options,
            [n1]: {
                ...options[n1],
                [n2]: value
            }
        };
    };
    useDelay(()=>{
        setScrollToPageId(otids[1]);
        setHasSkipped(true);
    }, skipFirstPage && otid[0] === otids[0] && !hasSkipped && inView ? 600 : null, [
        inView
    ]);
    options = merge_opt(options, 'zoom.mode', initialZoomMode);
    options = merge_opt(options, 'render.paginations', paginationMode);
    options = merge_opt(options, 'layout.gutter_x', gutterX);
    options = merge_opt(options, 'layout.gutter_y', gutterY);
    options = merge_opt(options, 'mouse.wheelMode', wheelMode);
    options = merge_opt(options, 'mouse.wheelModeMeta', wheelModeMeta);
    options = merge_opt(options, 'render.clsHover', S[clsHover]);
    if (options.zoom.mode == 'areaFit') {
        options.zoom.initialAlign = 'center';
    }
    if (isFullscreen) {
        // remove width/height restictions
        ;
        [width, height] = [
            '100vw',
            '100vh'
        ];
    }
    let [scrollToPageId, setScrollToPageId] = useState(null);
    useDelay(()=>{
        if (initAni == 'zoomAll') {
            setScrollToPageId({
                ids: 'ALL_IDS',
                duration: initAniDuration * 1000,
                scale: initAniScale
            });
        }
    }, inView ? initAniWait * 1000 || 1000 : null);
    let [viewerData, setViewerData] = useState({}) // shape {imageLocs, index}
    ;
    let { centerSelected, onCenterSelectionChange, onBackwardClick, onForwardClick } = useNavigation({
        navigationControls,
        viewerData,
        setScrollToPageId,
        optionSet
    });
    useEffect(()=>{
        if (onCenterChange && centerSelected) {
            onCenterChange(centerSelected);
        }
    }, [
        centerSelected
    ]);
    let onPageClick = null;
    if (imageClick == 'zoomToHeight') {
        onPageClick = ({ otid })=>setScrollToPageId({
                id: otid
            });
    }
    let { onViewerInstance, zoomIn, zoomOut } = useViewerControl();
    let renderCustomPaginations = ({ page, availableHeight, availableWidth })=>{
        // log.x({availableHeight, availableWidth})
        let text = page.pbox?.title || page.caption;
        return /*#__PURE__*/ _jsx("div", {
            style: {
                width: availableWidth,
                fontSize: '0.65em'
            },
            children: text
        });
    };
    let controls = null;
    if (zoomControls || navigationControls || fullscreenToggle) {
        controls = /*#__PURE__*/ _jsxs(ButtonGroup, {
            floated: "right",
            style: {
                zIndex: '20',
                position: 'absolute',
                right: '0',
                padding: '8px'
            },
            children: [
                zoomControls && /*#__PURE__*/ _jsx(ControlButton, {
                    icon: "minus",
                    onClickFunc: zoomOut
                }),
                zoomControls && /*#__PURE__*/ _jsx(ControlButton, {
                    icon: "plus",
                    onClickFunc: zoomIn
                }),
                !showFulltext && navigationControls && /*#__PURE__*/ _jsx(ControlButton, {
                    icon: "arrow left",
                    onClickFunc: onBackwardClick
                }),
                !showFulltext && navigationControls && /*#__PURE__*/ _jsx(ControlButton, {
                    icon: "arrow right",
                    onClickFunc: onForwardClick
                }),
                fullscreenToggle && /*#__PURE__*/ _jsx(ControlButton, {
                    icon: isFullscreen ? 'close' : 'expand',
                    onClickFunc: ()=>toggleFullscreen()
                })
            ]
        });
    }
    let fulltextControls = null;
    if (showFulltext) {
        fulltextControls = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx("div", {
                    onClick: onBackwardClick,
                    className: clsx(S.fullHeightNav, S.prev),
                    children: /*#__PURE__*/ _jsx(VIcon, {
                        name: "chevron left"
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    onClick: onForwardClick,
                    className: clsx(S.fullHeightNav, S.next),
                    children: /*#__PURE__*/ _jsx(VIcon, {
                        name: "chevron right"
                    })
                })
            ]
        });
    }
    let viewer = null;
    if (inView && (otid || size(otids))) {
        viewer = /*#__PURE__*/ _jsx(PageSetViewer, {
            otid: otid,
            otids: otids,
            optionSet: optionSet,
            options: options,
            scrollToPageId: scrollToPageId,
            flex: true,
            loadOnVisible: true,
            initialScrollTo: {
                id: otid
            },
            renderCustomPaginations: renderCustomPaginations,
            onCenterSelectionChange: onCenterSelectionChange,
            onPageClick: onPageClick,
            onDataLoaded: setViewerData,
            debug: false,
            onViewerInstance: onViewerInstance
        });
    }
    // XXX: this looks fishy
    const pageViewer = /*#__PURE__*/ _jsxs("div", {
        style: {
            overflow: 'hidden',
            height,
            width
        },
        className: S.pageViewer,
        ref: ref,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                ref: inviewRef,
                style: {
                    height,
                    minHeight: '250px',
                    width,
                    display: 'flex',
                    overflow: 'hidden',
                    position: 'relative'
                },
                children: [
                    controls,
                    fulltextControls,
                    addContent?.('viewer'),
                    viewer
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: S.caption,
                children: useCaptionLink(caption, linkid, otid, null, true)
            })
        ]
    });
    return showFulltext ? /*#__PURE__*/ _jsxs(SplitPane, {
        className: S.splitPane,
        borderless: true,
        vertical: true,
        children: [
            pageViewer,
            /*#__PURE__*/ _jsxs("div", {
                id: "presFulltext",
                className: S.fulltextContainer,
                children: [
                    addContent?.('fulltext'),
                    /*#__PURE__*/ _jsx(FulltextRenderPane, {
                        otid: centerSelected
                    })
                ]
            })
        ]
    }) : pageViewer;
};
